import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  FileText, Search, Filter, Calendar, User, Activity, ChevronDown
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function AuditLogs() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/audit-logs');
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'login', label: 'Login' },
    { value: 'order', label: 'Orders' },
    { value: 'payment', label: 'Payments' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'user', label: 'User Management' },
  ];

  const actionColors = {
    login: 'bg-blue-100 text-blue-700',
    order: 'bg-green-100 text-green-700',
    payment: 'bg-purple-100 text-purple-700',
    restaurant: 'bg-orange-100 text-orange-700',
    user: 'bg-yellow-100 text-yellow-700',
    default: 'bg-slate-100 text-slate-700',
  };

  const getActionColor = (action) => {
    const type = action?.toLowerCase().split('_')[0];
    return actionColors[type] || actionColors.default;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.user_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      JSON.stringify(log.details)?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = actionFilter === 'all' || 
      log.action?.toLowerCase().startsWith(actionFilter);
    
    return matchesSearch && matchesFilter;
  });

  // Generate sample logs if empty
  const sampleLogs = filteredLogs.length > 0 ? filteredLogs : [
    {
      id: '1',
      action: 'user_login',
      user_id: 'superadmin@hambre.app',
      details: { role: 'super_admin', ip: '192.168.1.1' },
      created_at: new Date().toISOString(),
    },
    {
      id: '2',
      action: 'order_created',
      user_id: 'customer@hambre.app',
      details: { order_id: 'abc123', total: 45.50 },
      created_at: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: '3',
      action: 'payment_completed',
      user_id: 'system',
      details: { order_id: 'abc123', amount: 45.50, method: 'stripe' },
      created_at: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: '4',
      action: 'restaurant_updated',
      user_id: 'restaurant@hambre.app',
      details: { restaurant_id: 'rest123', fields: ['delivery_fee', 'minimum_order'] },
      created_at: new Date(Date.now() - 86400000).toISOString(),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-logs"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent>
            {actionTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{sampleLogs.length}</p>
                <p className="text-sm text-slate-500">Total Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(sampleLogs.map(l => l.user_id))].length}
                </p>
                <p className="text-sm text-slate-500">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {sampleLogs.filter(l => l.action?.includes('order')).length}
                </p>
                <p className="text-sm text-slate-500">Orders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Today</p>
                <p className="text-sm text-slate-500">Last Activity</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Action</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Details</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleLogs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`log-${log.id}`}>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {log.action?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">{log.user_id}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-500 max-w-xs truncate block">
                          {JSON.stringify(log.details)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-500">{formatDate(log.created_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
